import * as React from 'react';
import { PaginationNavigation, SpinnerWrapper, SortTableContext } from "soprasteria-ui-library";
import { useAuth } from '../../../App';
import { GeneralDentalReferralSearchApi } from '../../../api/GeneralDentalReferralSearchApi';
import { IGeneralDentalReferral, IGeneralDentalReferralSearchParams } from '../../../models/IGeneralDentalReferral';
import { IPagination } from 'soprasteria-ui-library/dist/models/IPagination';
import { useHistory } from 'react-router';
import { GeneralDentalReferralSearchForm } from './GeneralDentalReferralSearchForm';
import { GeneralDentalReferralSearchTable } from './GeneralDentalReferralSearchTable';
import { isInRole, UserContext } from '../../../contexts/UserContext';
import { ALLMÄNTANDVÅRDBAS, LIBRETTOADMIN, TANDREGLERINGBAS } from '../../../utils/RoleConstants';
import { SortOrder } from '../../../models/IPage';
import { GENERAL_DENTAL_REFERRAL_SEARCH } from '../../../utils/RouteConstants';

export const GeneralDentalReferralSearch: React.FunctionComponent<{}> = () => {

    const auth = useAuth();
    const userInfo = React.useContext(UserContext);
    const api = new GeneralDentalReferralSearchApi(auth);
    const abortController = new AbortController();

    const [isFetchingGeneralDentalReferrals, setIsFetchingGeneralDentalReferrals] = React.useState<boolean>(false);
    const [paginationHeader, setPaginationHeader] = React.useState<IPagination>();
    const [generalDentalReferrals, setGeneralDentalReferrals] = React.useState<IGeneralDentalReferral[]>();
    const [searchParams, setSearchParams] = React.useState<IGeneralDentalReferralSearchParams>();
    const { sortOrder, selectedPropertyToSortBy, setSelectedPropertyToSortBy, setSortOrder } = React.useContext(SortTableContext);
    const history = useHistory();
    const pageSize = 20;
    const hasRoleAllmäntandvard = isInRole(ALLMÄNTANDVÅRDBAS, userInfo.roles ? userInfo.roles : []);
    const hasRoleOrtodontist = isInRole(TANDREGLERINGBAS, userInfo.roles ? userInfo.roles : []);
    const hasRoleSysAdmin = isInRole(LIBRETTOADMIN, userInfo.roles ? userInfo.roles : []);
    const defaultOrderBy = "SentDate";

    React.useEffect(() => {
        if (setSelectedPropertyToSortBy && setSortOrder) {
            if (history.location.state !== undefined && history.location.state !== null) {
                const lastSearchParams = history.location.state as IGeneralDentalReferralSearchParams;
                setSelectedPropertyToSortBy(lastSearchParams.propertyToOrderBy ?? defaultOrderBy);
                setSortOrder(lastSearchParams.sortOrder ?? SortOrder.descending);
                searchGeneralDentalReferrals(lastSearchParams);
            }
            else {
                setSelectedPropertyToSortBy(defaultOrderBy);
                setSortOrder(SortOrder.descending);
                searchGeneralDentalReferrals({ pageSize: pageSize, pageNumber: 1, propertyToOrderBy: defaultOrderBy, sortOrder: SortOrder.ascending });
            }
        }

        return () => {
            abortController.abort();
        }
    }, [])

    React.useEffect(() => {
        if (generalDentalReferrals) {
            onSortOrderClick();
        }
    }, [selectedPropertyToSortBy, sortOrder])

    const searchGeneralDentalReferrals = (data: IGeneralDentalReferralSearchParams) => {
        setIsFetchingGeneralDentalReferrals(true);
        data.pageSize = pageSize;
        data.pageNumber = data.pageNumber ?? 1;
        data.propertyToOrderBy = data.propertyToOrderBy ?? selectedPropertyToSortBy;
        data.sortOrder = data.sortOrder ?? sortOrder;
        history.push(GENERAL_DENTAL_REFERRAL_SEARCH, data);

        api.searchGeneralDentalReferrals(data, false, (response) => {
            setGeneralDentalReferrals(response.data);
            setPaginationHeader(response.paginationHeader);
        }, undefined, undefined, () => setIsFetchingGeneralDentalReferrals(false));

        setSearchParams(data);
    }

    const onPageSelect = (pageNumber: number) => {
        if (searchParams) {
            setIsFetchingGeneralDentalReferrals(true);
            var currentSearchParams = searchParams;
            currentSearchParams.pageNumber = pageNumber;
            history.push(GENERAL_DENTAL_REFERRAL_SEARCH, currentSearchParams);

            api.searchGeneralDentalReferrals(currentSearchParams, false, (response) => {
                setGeneralDentalReferrals(response.data);
                setPaginationHeader(response.paginationHeader);
            }, undefined, undefined, () => setIsFetchingGeneralDentalReferrals(false));
        }
    }

    const onSortOrderClick = () => {
        if (searchParams) {
            setIsFetchingGeneralDentalReferrals(true);
            var currentSearchParams = searchParams;
            currentSearchParams.pageNumber = 1;
            currentSearchParams.sortOrder = sortOrder;
            currentSearchParams.propertyToOrderBy = selectedPropertyToSortBy;
            history.push(GENERAL_DENTAL_REFERRAL_SEARCH, currentSearchParams);

            api.searchGeneralDentalReferrals(currentSearchParams, false, (response) => {
                setGeneralDentalReferrals(response.data);
                setPaginationHeader(response.paginationHeader);
            }, undefined, undefined, () => setIsFetchingGeneralDentalReferrals(false));
        }
    }

    return (
        <SpinnerWrapper isLoading={isFetchingGeneralDentalReferrals}>
            <section className="card">
                <div className="card-header">
                    <h1>Sök allmäntandvårdsremisser</h1>
                </div>
                <div className="card-body">
                    <GeneralDentalReferralSearchForm searchGeneralDentalReferrals={searchGeneralDentalReferrals} preSetSearchParams={history.location.state !== undefined && history.location.state !== null ? history.location.state as any : undefined} />
                    {
                        generalDentalReferrals ?
                            <>
                                <GeneralDentalReferralSearchTable generalDentalReferrals={generalDentalReferrals} hasRoleOrtodontist={hasRoleOrtodontist} hasRoleAllmäntandvård={hasRoleAllmäntandvard} hasRoleSysAdmin={hasRoleSysAdmin} />
                                <PaginationNavigation
                                    ariaLabel="received general dental referrals pagination selector"
                                    pagination={paginationHeader}
                                    pageNeighbours={2}
                                    selectedPage={page => onPageSelect(page)}
                                    showCount={true} />
                            </>
                            : null
                    }
                </div>
            </section>
        </SpinnerWrapper>
    )
}